import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Reports from audio → The Footage tab. Fixture `footageTab`: a
// finished report whose case file carries analyzed body-cam footage (24 s of
// synthetic test video, no real people). The Transcription tab is named
// "Footage" while the case has footage. Moments: arrival 0:01, vehicle 0:05,
// text read 0:07, contact 0:09, command 0:12, backpack 0:14 (clustered with keys 0:15), search
// 0:18, compliance 0:21, plus one footage-check finding at 0:03.
const CASE_ID = "c0000000-0000-4000-8000-000000000401";
const dot = (title: string, clock: string) => `${sel("footageMarker")}[title="${title} at ${clock}"]`;
const card = (id: string) => `${sel("footagePoint")}[data-point="event:${id}"]`;

const scene: Scene = {
  id: "the-footage-tab",
  title: "The Footage tab",
  startRoute: `/cases/${CASE_ID}`,
  fixture: "footageTab",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "When a case has analyzed footage, the Transcription tab turns into the Footage tab. I'll open it. You get the video, a marker bar, and a list of everything the analysis found.",
      actions: [
        { type: "waitFor", target: sel("editorTabTranscription") },
        // "I'll open it" is said at 4.9 s of 10.6 s.
        { type: "wait", ms: 4200 },
        { type: "click", target: sel("editorTabTranscription") },
        { type: "waitFor", target: sel("footageSeekbar") },
        { type: "wait", ms: 2500 },
      ],
    },
    {
      id: "markers",
      narration:
        "Along the bottom of the video is the marker bar. Each dot is a moment worth a look, and its color tells you the kind. Dimmed stretches are where nothing was happening.",
      actions: [
        { type: "moveTo", target: sel("footageSeekbar") },
        { type: "spotlight", target: sel("footageSeekbar") },
        { type: "wait", ms: 8000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "dot",
      narration:
        "Hover a dot and you see the still and what the analysis saw. Click it, and the video jumps to that moment. This one is where the backpack gets moved.",
      actions: [
        { type: "moveTo", target: dot("Property", "0:14"), hover: true },
        // "Click it" at 3.9 s of 8.7 s.
        { type: "wait", ms: 3300 },
        { type: "click", target: dot("Property", "0:14") },
        { type: "wait", ms: 3000 },
      ],
    },
    {
      id: "list",
      narration:
        "The list on the right says the same thing in words. It follows the playhead, and clicking any card seeks there. I'll click the search.",
      actions: [
        { type: "moveTo", target: sel("footagePoints") },
        // "I'll click the search" at 6.6 s of 7.8 s.
        { type: "wait", ms: 5500 },
        { type: "click", target: card("18000-search-0") },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "steps",
      narration: "The arrows in the player step through the points of interest in order. Next takes me to the next one.",
      actions: [
        { type: "moveTo", target: sel("footagePointNav") },
        // "Next takes me" at 3.8 s of 5.6 s.
        { type: "wait", ms: 2700 },
        { type: "click", target: sel("footageNextPoint") },
        { type: "wait", ms: 2000 },
      ],
    },
    {
      id: "filter",
      narration: "To find one kind of moment, use the filter. I'll pick Property, and the list narrows to every object that was handled.",
      actions: [
        { type: "moveTo", target: `${sel("footageKindFilter")} button` },
        // "I'll pick Property" at 3.1 s of 7.0 s.
        { type: "wait", ms: 1500 },
        { type: "click", target: `${sel("footageKindFilter")} button` },
        { type: "wait", ms: 700 },
        // A real click on an option moves focus into the menu, and the dropdown then
        // blurs it shut; the synthetic click cannot focus a <p>, so focus the menu first.
        { type: "click", target: `${sel("footageKindFilter")} .dropdown-content` },
        { type: "click", target: `${sel("footageKindFilter")} .dropdown-content li:nth-child(2) p` },
        { type: "wait", ms: 3000 },
      ],
    },
    {
      id: "context",
      narration:
        "Below the player, the transcript stays synced to what you're watching. In the rail, the summary says what the footage shows, and Footage check lists where the draft and the footage disagree.",
      actions: [
        { type: "moveTo", target: sel("footageTranscript") },
        { type: "spotlight", target: sel("footageTranscript") },
        // "In the rail" at 3.9 s, "Footage check" at 7.0 s of 10.4 s.
        { type: "wait", ms: 2800 },
        { type: "moveTo", target: sel("footageCheck") },
        { type: "spotlight", target: sel("footageAside") },
        { type: "wait", ms: 7000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "attach",
      narration:
        "To put a moment in the exported report, click its still in the strip at the bottom. It's attached, and clicking it again removes it. Attach the frame that backs up a disputed detail, not every frame.",
      actions: [
        { type: "scrollIntoView", target: sel("footageAttach") },
        { type: "moveTo", target: sel("footageAttach") },
        // "It's attached" at 5.0 s of 12.3 s.
        { type: "wait", ms: 2800 },
        { type: "click", target: `${sel("footageStill")}[title="Attach 0:16"]` },
        { type: "wait", ms: 4000 },
      ],
    },
  ],
};

export default scene;
